import React from "react";
import { BarChart3, FileText, ArrowRight, Calendar, Sparkles, Building2, TrendingUp } from "lucide-react";
import { getAllReports, ReportRecord } from "../../utils/reportRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface ReportHubPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function ReportHubPage({ onNavigate, onLogin = () => {}, currentUser }: ReportHubPageProps) {
  const reports = getAllReports();

  useSeoMetadata({
    title: "Local Employer & Market Data Reports | BizNearYou",
    description: "Data-driven reports analyzing local small business density, trade contractor momentum, contactability benchmarks, and digital presence quality.",
    canonicalUrl: "https://getbiznearyou.com/reports",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Local Employer & Market Data Report Directory",
      "itemListElement": reports.map((r, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": r.h1,
        "url": `https://getbiznearyou.com/reports/${r.slug}`
      }))
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs items={[{ label: "Reports", path: "/reports" }]} onNavigate={onNavigate} />

        {/* Hero Banner */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 mb-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Data-Driven Market Intelligence</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Local Small Business & Opportunity Reports
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Explore calculated, privacy-safe aggregate data reports evaluating small business density, contact responsiveness, trade contractor growth signals, and commercial web presence.
            </p>
          </div>
        </div>

        {/* Reports Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {reports.map((rep) => (
            <div
              key={rep.slug}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50">
                    {rep.reportingPeriod}
                  </span>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Calculated Q1 2026</span>
                  </span>
                </div>

                <h2 className="text-base font-bold text-slate-900 dark:text-white mb-2 leading-snug">
                  <a
                    href={`/reports/${rep.slug}`}
                    onClick={(e) => handleLinkClick(e, `/reports/${rep.slug}`)}
                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                  >
                    {rep.h1}
                  </a>
                </h2>

                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                  {rep.metaDescription}
                </p>

                {/* Data Points Snippet */}
                <div className="grid grid-cols-2 gap-2 mb-4 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-150 dark:border-slate-750">
                  {rep.dataPoints.slice(0, 2).map((dp, idx) => (
                    <div key={idx}>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider block truncate">{dp.label}</span>
                      <span className="text-sm font-extrabold text-indigo-600 dark:text-indigo-400">{dp.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-500 truncate max-w-[200px]">
                  {rep.sampleSizeSummary}
                </span>

                <a
                  href={`/reports/${rep.slug}`}
                  onClick={(e) => handleLinkClick(e, `/reports/${rep.slug}`)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 cursor-pointer flex-shrink-0"
                >
                  <span>View Full Report</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <SeoRelatedLinks currentRoute="/reports" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
